// frontend/src/components/SignInfoCard.jsx
import { getSignInfo } from '../data/zodiacInfo';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';



export default function SignInfoCard({ sign, info: infoProp, onBack }) {
  const info = infoProp || getSignInfo(sign);
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const cardRef = useRef(null);

  const translatedInfo = useMemo(() => {
    if (!info) return null;
    const signKey = String(sign || info.nombre || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    const key = `signInfoDetails.signs.${signKey}`;
    const translateArray = (field, fallback) => {
      const value = t(`${key}.${field}`, { returnObjects: true, defaultValue: fallback });
      return Array.isArray(value) ? value : fallback;
    };

    return {
      ...info,
      nombre: t(`${key}.name`, { defaultValue: info.nombre }),
      fechas: t(`${key}.dates`, { defaultValue: info.fechas }),
      elemento: t(`${key}.element`, { defaultValue: info.elemento }),
      modalidad: t(`${key}.modality`, { defaultValue: info.modalidad }),
      regente: t(`${key}.ruler`, { defaultValue: info.regente }),
      descripcion: t(`${key}.description`, { defaultValue: info.descripcion }),
      compatibles: translateArray('compatibilities', info.compatibles),
      opuesto: t(`${key}.opposite`, { defaultValue: info.opuesto }),
      fortalezas: translateArray('strengths', info.fortalezas),
      debilidades: translateArray('weaknesses', info.debilidades),
      leGusta: translateArray('likes', info.leGusta),
      noLeGusta: translateArray('dislikes', info.noLeGusta),
    };
  }, [info, sign, t]);

  // Fix Android/WebView viewport height (address bar / 100vh issues)
  useEffect(() => {
    const setVh = () => {
      try {
        const vh = (window.innerHeight || 0) * 0.01;
        document.documentElement.style.setProperty('--app-vh', `${vh}px`);
      } catch {}
    };

    setVh();
    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);
    return () => {
      window.removeEventListener('resize', setVh);
      window.removeEventListener('orientationchange', setVh);
    };
  }, []);

  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack();
      return;
    }
    try {
      navigate(-1);
    } catch (e) {
      if (typeof window !== 'undefined' && window.history) {
        window.history.back();
      }
    }
  };

  const compatList = useMemo(() => {
    const raw = translatedInfo?.compatibles;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map(String).map(s => s.trim()).filter(Boolean);
    // admite "Virgo, Capricornio" o "Virgo · Capricornio" etc
    return String(raw)
      .split(/,|\u00b7|\||\/|\n/)
      .map(s => String(s).trim())
      .filter(Boolean);
  }, [translatedInfo?.compatibles]);

  const opposite = useMemo(() => {
    const raw = translatedInfo?.opuesto;
    if (!raw) return null;
    const s = String(raw).trim();
    return s || null;
  }, [translatedInfo?.opuesto]);

  if (!translatedInfo) return null;

  return (
    <>
      <style>{`
        @keyframes spin360 {
          0%   { transform: translate(-50%, -50%) rotate(0deg); }
          100% { transform: translate(-50%, -50%) rotate(360deg); }
        }

        @keyframes signStarDrift {
          from { background-position: 22px 18px, 86px 44px, center; }
          to { background-position: 22px 130px, 86px 218px, center; }
        }

        @keyframes signGlowPulse {
          0%, 100% { opacity: 0.58; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.88; transform: translate(-50%, -50%) scale(1.02); }
        }
        @media (prefers-reduced-motion: reduce) { .no-spin { animation: none !important; } }

        #signInfoRoot::before {
          content: "";
          position: fixed;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          background-image:
            radial-gradient(circle, rgba(255,226,138,0.82) 0 1px, transparent 1.45px),
            radial-gradient(circle, rgba(255,255,255,0.46) 0 1px, transparent 1.35px),
            linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.58));
          background-size: 112px 112px, 174px 174px, 100% 100%;
          background-position: 22px 18px, 86px 44px, center;
          opacity: 0.48;
          animation: signStarDrift 18s linear infinite;
        }


        #signInfoRoot .sign-card::after {
          content: "";
          position: absolute;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          background:
            radial-gradient(circle at 18% 12%, rgba(218,165,32,0.12), transparent 23%),
            radial-gradient(circle at 88% 62%, rgba(174,65,255,0.14), transparent 34%);
        }

        #signInfoRoot .sign-card > * {
          position: relative;
          z-index: 2;
        }

        #signInfoRoot .panel-box {
          position: relative;
          overflow: hidden;
        }

        #signInfoRoot .panel-box::before {
          content: "";
          position: absolute;
          inset: 0;
          pointer-events: none;
          border-radius: inherit;
          background: radial-gradient(circle at 20% 18%, rgba(255,216,118,0.08), transparent 28%);
        }


        /* Android/WebView: altura real del viewport (evita que el contenido quede “abajo” o cortado) */
        #signInfoRoot {
          height: calc(var(--app-vh, 1vh) * 100);
        }

        /* Tipografía consistente entre iOS/Android + evitar auto-zoom de texto en Android */
        #signInfoRoot {
          font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji";
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: geometricPrecision;
          -webkit-text-size-adjust: 100%;
          text-size-adjust: 100%;
        }

        /* Android/WebView: algunos dispositivos aplican “Text scaling”. Lo neutralizamos dentro de esta screen. */
        #signInfoRoot h1,
        #signInfoRoot h2,
        #signInfoRoot h3,
        #signInfoRoot h4,
        #signInfoRoot p,
        #signInfoRoot li,
        #signInfoRoot span,
        #signInfoRoot small,
        #signInfoRoot button {
          font-kerning: normal;
          letter-spacing: normal;
        }

        /* No heredamos velos/mezclas que opaquen la tarjeta */
        #signInfoRoot, #signInfoRoot * {
          background-blend-mode: normal !important;
          mix-blend-mode: normal !important;
        }

        /* Evitar que imágenes/fondos se “pixelen” o se sobreexpongan en algunos Android */
        #signInfoRoot {
          overscroll-behavior: none;
        }

        /* Asegurar que la rueda de fondo no “tinte” el contenido */
        #signInfoRoot .sign-card {
          -webkit-transform: translateZ(0);
          transform: translateZ(0);
        }

        /* No tocar el fondo del Chat ni otras pantallas aquí */

        /* Neutralizar cualquier ruleta o capa con blur heredada en esta screen */
        #signInfoRoot [class*="blur"],
        #signInfoRoot [class*="bg__blur"],
        #signInfoRoot [data-bg="blur"],
        #signInfoRoot [class*="wheel"]:not([data-wheel="primary"]) {
          background: transparent !important;
          filter: none !important;
          opacity: 0 !important;
          display: none !important;
        }
        /* Mobile: layout estable y scrolleable (Android/iOS). Evita lock de scroll por position:fixed + overflow) */
        /* Mobile (iOS/Android): scroll libre + card centrada (sin romper desktop) */
@media (max-width: 600px) {
  /* Importante: en Android suele romperse si el root scrollea con fondos fixed.
     Dejamos que scrollee el documento. */
  html, body {
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
    height: auto !important;
  }

  /* Root: que no tenga scroll interno ni altura fija que “clava” la vista */
  #signInfoRoot {
    height: auto !important;
    min-height: calc(var(--app-vh, 1vh) * 100) !important;
    overflow: visible !important;
    display: block !important;

    padding-top: max(4px, env(safe-area-inset-top));
    padding-bottom: max(12px, env(safe-area-inset-bottom));
    padding-left: 12px;
    padding-right: 12px;

    touch-action: pan-y;
  }

  /* Contenedor del contenido: centrado horizontalmente (ajuste iOS específico más abajo) */
  #signInfoRoot .sign-content {
    justify-content: center !important;
    padding-top: max(0px, env(safe-area-inset-top));
    padding-bottom: max(12px, env(safe-area-inset-bottom));
    transform: translateY(-48px);
  }

  /* iOS: evitar que la card quede pegada arriba */
  @supports (-webkit-touch-callout: none) {
    #signInfoRoot .sign-content {
      align-items: center !important;
      min-height: calc(var(--app-vh, 1vh) * 100);
    }
  }

  /* La card NO ocupa toda la pantalla: queda centrada, con aire, y scrollea el body */
  #signInfoRoot .sign-card {
    width: 100% !important;
    max-width: 520px !important;
    margin: -8px auto 8px !important;

    max-height: calc((var(--app-vh, 1vh) * 100) - 28px) !important;
    overflow: hidden !important;

    padding: 44px 14px 12px !important;
    border-radius: 22px !important;
  }


  #signInfoRoot .sign-card h3 { font-size: 1.52rem !important; line-height: 0.98 !important; }
  #signInfoRoot .sign-card p  { font-size: 0.76rem !important; line-height: 1.24 !important; }
  #signInfoRoot .sign-card li { font-size: 0.72rem !important; line-height: 1.24 !important; }

  #signInfoRoot .sign-card button {
    min-height: 38px !important;
    font-size: 0.86rem !important;
  }

  #signInfoRoot .signHeader {
    gap: 10px !important;
    margin-bottom: 8px !important;
    align-items: center !important;
  }

  #signInfoRoot .signTitleRow {
    justify-content: center !important;
    gap: 8px !important;
  }

  #signInfoRoot .signDatePill {
    padding: 5px 10px !important;
    font-size: 0.75rem !important;
  }

  #signInfoRoot .signMetaRow {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 5px !important;
    margin-top: 7px !important;
  }

  #signInfoRoot .signMetaPill {
    padding: 6px 5px !important;
    font-size: 0.58rem !important;
    gap: 3px !important;
    flex-direction: column !important;
  }

  #signInfoRoot .signDesc {
    margin-top: 8px !important;
    margin-bottom: 6px !important;
    max-width: none !important;
  }

  #signInfoRoot .signDivider {
    margin: 7px 0 6px !important;
  }

  #signInfoRoot .chipsBlock {
    margin-top: 6px !important;
  }

  #signInfoRoot .chipsWrap {
    gap: 4px !important;
  }

  #signInfoRoot .chipsLabel {
    font-size: 0.68rem !important;
    letter-spacing: 0.1em !important;
  }

  #signInfoRoot .chipsRow {
    gap: 5px !important;
  }

  #signInfoRoot .chipPill {
    padding: 5px 8px !important;
    font-size: 0.7rem !important;
  }

  #signInfoRoot .infoGrid {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 7px !important;
    margin-top: 8px !important;
  }

  #signInfoRoot .panel-box {
    padding: 10px 11px 9px !important;
    border-radius: 15px !important;
  }

  #signInfoRoot .panel-box h4 {
    font-size: 0.68rem !important;
    margin-bottom: 5px !important;
    letter-spacing: 0.1em !important;
  }

  #signInfoRoot .panel-box ul {
    padding-left: 14px !important;
  }

  #signInfoRoot .topBackBtn {
    top: 12px !important;
    left: 14px !important;
    min-height: 30px !important;
    padding: 0 13px !important;
    font-size: 0.76rem !important;
  }

  #signInfoRoot .sign-card [style*="grid-template-columns: repeat(3"] {
    grid-template-columns: 1fr !important;
  }

  #signInfoRoot .sign-card [style*="grid-template-columns: 1fr 1fr"] {
    grid-template-columns: 1fr !important;
  }

  #signInfoRoot .sign-card [style*="width: 96px"] {
    width: 68px !important;
    height: 68px !important;
    font-size: 36px !important;
  }

  /* Pantallas muy angostas: grilla a 1 columna */
  @media (max-width: 380px) {
    #signInfoRoot .sign-card [data-grid="two"] {
      grid-template-columns: 1fr !important;
    }
  }
}
      `}</style>

      <div id="signInfoRoot" style={page}>
        {/* Fondo de relleno: gradiente oscuro + misma imagen blur, para que nunca quede blanco */}
        <div style={bgFill} aria-hidden="true" />

        {/* Capa de cobertura: misma imagen en cover + blur para rellenar toda la pantalla */}
        <div style={bgCoverBlur} aria-hidden="true" />

        {/* ÚNICA ruleta de fondo, centrada y recortada perfectamente en círculo */}
        <div style={bgWheel} data-wheel="primary" aria-hidden="true" />
        {/* Vignette/feather para integrar la rueda y evitar “quemados” */}
        <div style={edgeFeather} aria-hidden="true" />

        {/* Contenido centrado */}
<div style={contentWrap} className="sign-content">
          <div ref={cardRef} style={card} className="sign-card">
            <button type="button" onClick={handleBack} className="topBackBtn" style={topBackBtn}>
              ← {t('actions.back')}
            </button>

            <div className="signHeader" style={header}>
              <span style={emojiBadge}>{translatedInfo.emoji}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="signTitleRow" style={titleRow}>
                  <h3 style={title}>{translatedInfo.nombre}</h3>
                  <span className="signDatePill" style={datePill}>📅 {translatedInfo.fechas}</span>
                </div>
                <div className="signMetaRow" style={metaRow}>
                  <span className="signMetaPill" style={metaPill}>△ <b>{t('signInfoDetails.labels.element')}</b><em>{translatedInfo.elemento}</em></span>
                  <span className="signMetaPill" style={metaPill}>● <b>{t('signInfoDetails.labels.modality')}</b><em>{translatedInfo.modalidad}</em></span>
                  <span className="signMetaPill" style={metaPill}>♄ <b>{t('signInfoDetails.labels.ruler')}</b><em>{translatedInfo.regente}</em></span>
                </div>
              </div>
            </div>

            <p className="signDesc" style={desc}>{translatedInfo.descripcion}</p>
            <div className="signDivider" aria-hidden="true" style={divider}><span /> <i>✦</i> <span /></div>

            {(compatList.length > 0 || opposite) && (
              <div className="chipsBlock" style={{ marginTop: 12 }}>
                {compatList.length > 0 && (
                  <div className="chipsWrap" style={chipsWrap}>
                    <span className="chipsLabel" style={chipsLabel}>↝ {t('signInfoDetails.labels.compatibilities')}</span>
                    <div className="chipsRow" style={chipsRow}>
                      {compatList.slice(0, 8).map((name) => (
                        <span key={name} className="chipPill" style={chip}>
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {opposite && (
                  <div className="chipsWrap" style={{ ...chipsWrap, marginTop: 10 }}>
                    <span className="chipsLabel" style={{ ...chipsLabel, color: '#C77DFF' }}>↪ {t('signInfoDetails.labels.opposite')}</span>
                    <div className="chipsRow" style={chipsRow}>
                      <span className="chipPill" style={{ ...chip, borderColor: 'rgba(203,182,255,0.55)', color: '#E9E0FF' }}>
                        {opposite}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="infoGrid" style={grid} data-grid="two">
              <section className="panel-box" style={{ ...panelBox, borderColor: 'rgba(255,218,116,0.42)' }}>
                <h4 style={{ ...h4, color: '#FFD979' }}>◎ {t('signInfoDetails.labels.strengths')}</h4>
                <ul style={ul}>
                  {translatedInfo.fortalezas.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </section>
              <section className="panel-box" style={{ ...panelBox, borderColor: 'rgba(199,125,255,0.42)' }}>
                <h4 style={{ ...h4, color: '#C77DFF' }}>◈ {t('signInfoDetails.labels.weaknesses')}</h4>
                <ul style={ul}>
                  {translatedInfo.debilidades.map((d) => (
                    <li key={d}>{d}</li>
                  ))}
                </ul>
              </section>
            </div>

            <div className="infoGrid" style={grid} data-grid="two">
              <section className="panel-box" style={{ ...panelBox, borderColor: 'rgba(125,235,144,0.34)' }}>
                <h4 style={{ ...h4, color: '#8EEB9D' }}>♡ {t('signInfoDetails.labels.likes')}</h4>
                <ul style={ul}>
                  {translatedInfo.leGusta.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </section>
              <section className="panel-box" style={{ ...panelBox, borderColor: 'rgba(255,101,126,0.38)' }}>
                <h4 style={{ ...h4, color: '#FF7189' }}>× {t('signInfoDetails.labels.dislikes')}</h4>
                <ul style={ul}>
                  {translatedInfo.noLeGusta.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </section>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}

const card = {
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(180deg, rgba(18,5,42,0.74), rgba(7,2,21,0.82))',
  borderRadius: 32,
  padding: '52px 42px 30px',
  border: '1px solid rgba(232,95,255,0.34)',
  color: '#FFF6D8',
  boxShadow: '0 24px 70px rgba(0,0,0,0.5), 0 0 34px rgba(171,54,255,0.2), inset 0 1px 0 rgba(255,255,255,0.06)',
  marginBottom: 12,
  maxWidth: 'min(940px, 100%)',
  boxSizing: 'border-box',
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
};

const header = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 22,
  marginBottom: 22,
};

const emojiBadge = {
  width: 96,
  height: 96,
  borderRadius: '50%',
  display: 'grid',
  placeItems: 'center',
  fontSize: 52,
  lineHeight: 1,
  background: 'radial-gradient(circle at 35% 24%, rgba(190,80,255,0.78), rgba(35,6,70,0.9) 64%, rgba(5,1,18,0.96) 100%)',
  border: '2px solid rgba(255,218,116,0.9)',
  boxShadow: '0 0 28px rgba(218,165,32,0.36), 0 0 26px rgba(190,80,255,0.3), inset 0 0 14px rgba(255,218,116,0.08)',
  color: '#FFD979',
  flexShrink: 0,
  textShadow: '0 0 18px rgba(218,165,32,0.45)',
};

const title = {
  margin: 0,
  color: '#FFD979',
  fontWeight: 500,
  fontSize: 'clamp(2.4rem, 6vw, 3.65rem)',
  lineHeight: 1,
  fontFamily: "'Cinzel', 'Cormorant Garamond', serif",
  textShadow: '0 0 22px rgba(218,165,32,0.18)',
};

const titleRow = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  flexWrap: 'wrap',
};

const datePill = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '9px 16px',
  borderRadius: 999,
  border: '1px solid rgba(199,125,255,0.32)',
  background: 'rgba(38,10,72,0.42)',
  color: '#D8C8FF',
  fontSize: 15,
  fontWeight: 800,
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
};

const metaRow = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 10,
  marginTop: 14,
};

const metaPill = {
  minWidth: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '10px 12px',
  borderRadius: 999,
  border: '1px solid rgba(199,125,255,0.24)',
  background: 'rgba(15,4,38,0.46)',
  color: '#FFF6D8',
  fontSize: 13,
  lineHeight: 1.15,
  boxSizing: 'border-box',
};


const desc = {
  marginTop: 16,
  marginBottom: 12,
  lineHeight: 1.48,
  fontSize: '1rem',
  color: 'rgba(255,255,255,0.86)',
  maxWidth: 610,
};

const divider = {
  width: '100%',
  margin: '18px 0 14px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  color: '#FFD979',
};

const chipsWrap = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const chipsLabel = {
  fontSize: 14,
  fontWeight: 900,
  color: '#FFD979',
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
};

const chipsRow = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  alignItems: 'center',
};

const chip = {
  padding: '8px 14px',
  borderRadius: 999,
  border: '1px solid rgba(218,165,32,0.38)',
  background: 'rgba(0,0,0,0.26)',
  color: '#FFF6D8',
  fontSize: 14,
  fontWeight: 800,
  lineHeight: 1,
  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
};

const grid = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 14,
  marginTop: 14,
};

const panelBox = {
  borderRadius: 22,
  padding: '22px 22px 20px',
  border: '1px solid rgba(199,125,255,0.3)',
  background: 'linear-gradient(180deg, rgba(20,5,42,0.42), rgba(8,2,24,0.44))',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
};

const h4 = {
  margin: '0 0 12px',
  fontSize: '1.05rem',
  fontWeight: 900,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
};

const ul = {
  margin: 0,
  paddingLeft: 20,
  lineHeight: 1.55,
  color: 'rgba(255,255,255,0.88)',
};

const topBackBtn = {
  position: 'absolute',
  top: 16,
  left: 18,
  zIndex: 4,
  minHeight: 34,
  padding: '0 15px',
  borderRadius: 999,
  border: '1px solid rgba(232,95,255,0.58)',
  background: 'rgba(14,4,31,0.68)',
  color: '#FFE08B',
  fontWeight: 900,
  fontSize: '0.82rem',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  cursor: 'pointer',
  boxShadow: '0 0 18px rgba(190,60,255,0.28), inset 0 0 14px rgba(218,165,32,0.06)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
};

const page = {
  minHeight: 'calc(var(--app-vh, 1vh) * 100)',
  width: '100%',
  position: 'relative',
  overflow: 'visible',
  WebkitOverflowScrolling: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'clamp(4px, 2vw, 16px)',
  isolation: 'isolate',
  background: 'radial-gradient(circle at 50% 30%, rgba(110,24,184,0.32) 0%, rgba(30,0,70,0.48) 32%, rgba(4,1,15,0.98) 74%), linear-gradient(180deg, #05010F 0%, #150028 50%, #04000D 100%)',
};

const bgFill = {
  position: 'fixed',
  inset: 0,
  zIndex: -1,
  pointerEvents: 'none',
  background:
    'radial-gradient(circle at 50% 30%, rgba(110,24,184,0.32) 0%, rgba(30,0,70,0.48) 32%, rgba(4,1,15,0.98) 74%), linear-gradient(180deg, #05010F 0%, #150028 50%, #04000D 100%)',
};

/* Capa de cobertura: transparente sin blur ni gradiente para evitar “segunda ruleta” */
const bgCoverBlur = {
  position: 'fixed',
  inset: '-3vmax',
  zIndex: 0,
  pointerEvents: 'none',
  background: 'transparent',
  filter: 'none',
  opacity: 0,
  transform: 'scale(1.02)',
  willChange: 'transform, filter, opacity'
};

/* Única ruleta nítida, a pantalla completa, centrada y sin bordes negros */
const bgWheel = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  width: 'min(92vmin, 92vw, 92vh)',
  height: 'min(92vmin, 92vw, 92vh)',
  transform: 'translate(-50%, -50%)',
  borderRadius: '50%',
  backgroundImage: 'url("/logo/Zodiacus.jpeg")',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center center',
  backgroundSize: 'contain',
  animation: 'spin360 160s linear infinite',
  willChange: 'transform',
  transformOrigin: 'center center',
  filter: 'saturate(1.05) contrast(1.04) brightness(1.08) drop-shadow(0 0 40px rgba(154,47,255,0.24))',
  opacity: 0.2,
  pointerEvents: 'none',
  zIndex: 1,
  WebkitMaskImage: 'radial-gradient(circle at 50% 50%, rgba(0,0,0,1) 96%, rgba(0,0,0,0) 100%)',
  maskImage: 'radial-gradient(circle at 50% 50%, rgba(0,0,0,1) 96%, rgba(0,0,0,0) 100%)',
};

const edgeFeather = {
  position: 'fixed',
  inset: 0,
  zIndex: 2,
  pointerEvents: 'none',
  background:
    'radial-gradient(70% 62% at 50% 45%, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.12) 55%, rgba(0,0,0,0.48) 100%)',
};

const contentWrap = {
  position: 'relative',
  zIndex: 3,
  width: '100%',
  maxWidth: 980,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: 'max(2px, env(safe-area-inset-top))',
  paddingBottom: 'max(10px, env(safe-area-inset-bottom))',
  transform: 'translateY(-42px)',
};
